/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import {useCallback, useMemo} from 'react';
import {useQueryClient} from 'react-query';
import isEmpty from 'lodash.isempty';
import {getDevice} from '@ion/core/src/services/Connect';
import Device from '@ion/core/src/services/Device';
import {getSensorData} from '@ion/core/src/services/MicroServices';
import {
  AugmentedSmarterTrailerSensorData,
  DeviceFeatureSensorTypes,
  EntityStatus, SensorTypes
} from '@ion/core/src/types/entities';
import {createKey, QueryKey} from '../config/QueryKeys';
import {createQueryResult, getStaleTime} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';
import useSearchById from './useSearchById';

// defines the prop definitions for useLoadSmartTrailer...
export interface UseLoadSmartTrailer {
  device: Device
  Features: any,
  augmentSensorData: Function;
  staleTime?: number;
};

// defines the return type of UseLoadSmartTrailer hook result...
export type UseLoadSmartTrailerResult = UseDefaultQueryResult & {
  result: AugmentedSmarterTrailerSensorData;
  isLoading: boolean;
  refresh: Function;
};

// defines the max result enumeration...
export enum MAX_RESULTS {
  DOOR_SENSOR_DATA = 4,
  SENSOR_DATA = 2
};

// checks for which sensor apps the user has and returns an array of
// the ones they do have.
const hasSensorApps = (Features): SensorTypes[] => {
  let apps = [];
  if (Features.hasSmartTrailer()) {
    apps = Object.values(SensorTypes);
  }
  return apps;
};

// this function compares the device's features with the user's approved
// apps to make sure we are only asking for what the device has enabled.
const getAvailableSensorApps = (userApps, features) => {
  return features ? userApps.filter(app => (
    features[DeviceFeatureSensorTypes[app]] === EntityStatus.Enabled
  )) : userApps;
};

// defines a utility to validate if the same ESN
const isSameESN = (deviceEsn: string, {rawData = []} = {}) => {
  return rawData.length ? (
    deviceEsn === rawData[0].sensorData?.mobileId
  ) : false;
};

// this function will fetch the sensor data based on the applications...
const getSensorDataByESN = async({esn, apps, maxResults = 1}) => {
  return await getSensorData({
    numberOfRecords : maxResults,
    mobileId        : esn,
    apps            : apps
  });
};

/**
 * The useLoadSmartTrailer hook is the responsible for loading smart trailer data from the service provider.
 * This hook fetch various data of the smart trailer and returned to the client app that can be usable by
 * them.
 *
 * ```typescript
 *
 *  import {useLoadSmartTrailer} from '@ion/query/src/hooks/useLoadSmartTrailer';
 *
 *  const {
 *    result: selectedAssetTrailer,
 *    isLoading: smartTrailerLoading
 *  } = useLoadSmartTrailer({
 *    augmentSensorData,
 *    device,
 *    Features
 *  });
 *
 * ```
 */
export const useLoadSmartTrailer = ({
  device,
  Features,
  augmentSensorData,
  staleTime = getStaleTime(15, false, false) as number
}: UseLoadSmartTrailer) => {
  const queryClient = useQueryClient();
  const esn         = device?.getDevice ? device?.getDevice()?.deviceEsn : null;
  const enabled     = device?.isCVSAsset ? device?.isCVSAsset() : false;

  const {
    result: devices,
    isLoading : isDeviceLoading
  } = useSearchById({
    api     : async(esn) => await getDevice(parseInt(esn), false, true),
    id      : esn,
    baseKey : QueryKey.DEVICE_GET_BY_ESN,
    enabled : Boolean(enabled && esn),
    staleTime
  });

  const {features} = useMemo(() => (
    (!isEmpty(devices) ? devices[0] : devices) || {}
  ), [devices]);

  let sensorApps         = hasSensorApps(Features);
  sensorApps             = getAvailableSensorApps(sensorApps, features);
  const concatSensorApps = sensorApps.filter(app => app !== SensorTypes.DoorState && app !== SensorTypes.BMS).join(', ');
  const hasDoorSensor    = sensorApps.includes(SensorTypes.DoorState);
  const hasBrakeSensor   = sensorApps.includes(SensorTypes.BMS);

  const {
    result: doorSensorData,
    isLoading: isDoorSensorLoading
  } = useSearchById({
    api : async(esn) => await getSensorDataByESN({
      esn,
      apps       : SensorTypes.DoorState,
      maxResults : MAX_RESULTS.DOOR_SENSOR_DATA
    }),
    enabled : Boolean(enabled && hasDoorSensor && esn && !isEmpty(features) && sensorApps?.length),
    baseKey : QueryKey.DOOR_SENSOR_DATA,
    id      : esn,
    staleTime
  });

  const {
    result: brakeSensorData,
    isLoading: isBrakeSensorLoading
  } = useSearchById({
    api : async(esn) => await getSensorDataByESN({
      esn,
      apps       : SensorTypes.BMS,
      maxResults : 5
    }),
    enabled : Boolean(enabled && hasBrakeSensor && esn && !isEmpty(features) && sensorApps?.length),
    baseKey : QueryKey.BRAKE_SENSOR_DATA,
    id      : esn,
    staleTime
  });

  const sensorData: any = useSearchById({
    api : async(esn) => await getSensorDataByESN({
      esn,
      apps : concatSensorApps
    }),
    enabled : Boolean(enabled && esn && !isEmpty(features) && sensorApps?.length),
    baseKey : QueryKey.SENSOR_DATA,
    id      : esn,
    staleTime
  });

  const isLoading  = sensorData.isLoading || isDeviceLoading || isDoorSensorLoading || isBrakeSensorLoading;

  const finalData = useMemo(() => {
    if (!isEmpty(sensorData?.result)) {
      return !isEmpty(doorSensorData)
        ? sensorData?.result?.concat(doorSensorData, brakeSensorData)
        : sensorData?.result;
    }
    return doorSensorData;
  }, [doorSensorData, brakeSensorData, sensorData?.result]);

  const augmentedData = useMemo(() => {
    if (esn && device && !isLoading && enabled && !isEmpty(finalData)) {
      return augmentSensorData(
        device.getDevice(),
        finalData,
        isSameESN(esn as string, device as any)
      );
    }
    return null;
  }, [isLoading, device, esn, enabled, finalData]);

  return createQueryResult(sensorData, {
    result : augmentedData,
    isLoading,
    enabled
  }) as UseLoadSmartTrailerResult;
};

export default useLoadSmartTrailer;
